import { useState, useEffect } from 'react';

import api from '@/services/api';

/**
 * Theme layout type
 */
type CvThemeLayout = 'single' | 'sidebar-left' | 'sidebar-right' | 'banner' | 'grid';

/**
 * Theme registry entry (frontend shape from GET /cv/themes)
 */
interface CvThemeEntry {
  id: string;
  source: 'npm' | 'vendored';
  packageName: string;
  label: string;
  license: string;
  atsSafe: boolean;
  layout: CvThemeLayout;
  featured: boolean;
  enabled: boolean;
  position: number;
}

/**
 * Fetch enabled CV themes for the cabine picker
 */
export function useCvThemes(enabled = true) {
  const [data, setData] = useState<CvThemeEntry[] | null>(null);
  const [isLoading, setIsLoading] = useState(enabled);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!enabled) {
      setData(null);
      setIsLoading(false);
      return;
    }

    let cancelled = false;

    async function fetchThemes() {
      try {
        const themes = await api.get<CvThemeEntry[]>('/cv/themes');
        if (!cancelled) {
          setData(themes);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err : new Error(String(err)));
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    }

    fetchThemes();

    return () => {
      cancelled = true;
    };
  }, [enabled]);

  return { data, isLoading, error };
}

/**
 * Fetch rendered HTML for a CV payload with a given theme
 */
export async function fetchCvThemePreviewHtml(
  cv: unknown,
  themeId: string,
  params?: Record<string, unknown>,
): Promise<string> {
  const result = await api.post<
    { html: string },
    { cv: unknown; themeId: string; params?: Record<string, unknown> }
  >('/cv/preview-payload', { cv, themeId, params });
  return result.html;
}

export type { CvThemeEntry, CvThemeLayout };
